import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { API_BASE as API, authHeaders } from "../api/api";

const CART_KEY = "hr_cart";

const loadLocalCart = () => {
  try {
    const raw = localStorage.getItem(CART_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

// Server CartItemRead -> the legacy client cart-line shape (product fields
// + qty, keyed by product id) — Cart.jsx/Checkout.jsx/etc. have always
// expected this shape and are not touched by the server-cart migration.
const toLegacyShape = (items) =>
  items.map((i) => ({
    id: i.product_id,
    name: i.name,
    price: i.price,
    category: i.category,
    image_url: i.image_url,
    is_preorder: i.is_preorder,
    weight_kg: i.weight_kg,
    qty: i.qty,
  }));

/**
 * Cart state, backed by localStorage while logged out (unchanged from
 * before) and by the server (`/cart*`) once logged in. Components consume
 * the same `cart` array shape either way — see toLegacyShape above.
 */
export function useCart() {
  const { user, token } = useAuth();
  const [cart, setCart] = useState(loadLocalCart);
  const [cartError, setCartError] = useState(null);
  const mergedForUserRef = useRef(null);

  // Logged-out: persist to localStorage exactly as before.
  useEffect(() => {
    if (user) return;
    try {
      localStorage.setItem(CART_KEY, JSON.stringify(cart));
    } catch {
      // ignore — cart just won't survive a refresh
    }
  }, [cart, user]);

  const resyncFromServer = useCallback(async () => {
    try {
      const res = await fetch(`${API}/cart`, { headers: authHeaders(token) });
      if (res.ok) {
        const data = await res.json();
        setCart(toLegacyShape(data.items));
      }
    } catch {
      // best-effort — cart state just stays as it was
    }
  }, [token]);

  // Logged-in: server cart is the source of truth. On the login
  // transition specifically (once per user), merge any leftover
  // localStorage cart into it — additive, never replacing what the
  // server already has — then clear localStorage.
  useEffect(() => {
    if (!user || !token) {
      mergedForUserRef.current = null;
      return;
    }
    if (mergedForUserRef.current === user.id) return;
    mergedForUserRef.current = user.id;

    (async () => {
      const localCart = loadLocalCart();
      if (localCart.length) {
        await fetch(`${API}/cart/merge`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...authHeaders(token) },
          body: JSON.stringify(localCart.map((i) => ({ product_id: i.id, qty: i.qty }))),
        }).catch(() => {});
        try {
          localStorage.removeItem(CART_KEY);
        } catch {
          // ignore
        }
      }
      resyncFromServer();
    })();
  }, [user, token, resyncFromServer]);

  const handleAddToCart = useCallback(
    (product, qty = 1) => {
      setCart((prev) => {
        const existing = prev.find((i) => i.id === product.id);
        if (existing) {
          return prev.map((i) => (i.id === product.id ? { ...i, qty: i.qty + qty } : i));
        }
        return [...prev, { ...product, qty }];
      });
      if (!(user && token)) return;
      fetch(`${API}/cart/items`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders(token) },
        body: JSON.stringify({ product_id: product.id, qty }),
      })
        .then((res) => (res.ok ? res.json() : Promise.reject(res)))
        .then((data) => setCart(toLegacyShape(data.items)))
        .catch(async (res) => {
          // Surface the age-restriction rejection specifically — every
          // other rejection (e.g. the 5-qty cap) stays a silent resync,
          // matching prior behavior.
          if (res?.status === 403) {
            try {
              const body = await res.json();
              if (body?.detail === "age_restricted") {
                setCartError("This item requires a signed-in account with a verified adult age.");
                setTimeout(() => setCartError(null), 4000);
              }
            } catch {
              // ignore — fall through to resync below regardless
            }
          }
          resyncFromServer(); // e.g. the 5-qty cap rejected it — resync to the real state
        });
    },
    [user, token, resyncFromServer]
  );

  const handleRemove = useCallback(
    (id) => {
      setCart((prev) => prev.filter((i) => i.id !== id));
      if (user && token) {
        fetch(`${API}/cart/items/${id}`, { method: "DELETE", headers: authHeaders(token) }).catch(() => {});
      }
    },
    [user, token]
  );

  const handleClear = useCallback(() => {
    setCart([]);
    if (user && token) {
      fetch(`${API}/cart`, { method: "DELETE", headers: authHeaders(token) }).catch(() => {});
    } else {
      try {
        localStorage.removeItem(CART_KEY);
      } catch {
        // ignore
      }
    }
  }, [user, token]);

  return { cart, handleAddToCart, handleRemove, handleClear, cartError };
}
